import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, RefreshCw, XCircle, Clock } from "lucide-react";
import { formatDistanceToNow, differenceInDays } from "date-fns";
import { toast } from "sonner";
import { useState } from "react";

interface RenewalTrackerProps {
  dealId: string;
  endDate: string | null;
}

interface Renewal {
  id: string;
  renewal_status: string;
  renewal_probability: number | null;
  next_reminder_at: string | null;
  notes: string | null;
  contacted_at: string | null;
}

export const RenewalTracker = ({ dealId, endDate }: RenewalTrackerProps) => {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");

  const { data: renewal, isLoading } = useQuery({
    queryKey: ["contract-renewal", dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_renewals" as any)
        .select("*")
        .eq("deal_id", dealId)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as Renewal | null;
    },
  });

  const updateRenewalMutation = useMutation({
    mutationFn: async (data: { status: string; notes?: string }) => {
      const { error } = await supabase
        .from("contract_renewals" as any)
        .update({
          renewal_status: data.status,
          notes: data.notes,
          decision_at: new Date().toISOString(),
        })
        .eq("id", renewal!.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Renewal status updated");
      queryClient.invalidateQueries({ queryKey: ["contract-renewal", dealId] });
      setNotes("");
    },
    onError: () => {
      toast.error("Failed to update renewal status");
    },
  });

  if (isLoading || !endDate) {
    return null;
  }

  const daysUntilExpiration = differenceInDays(new Date(endDate), new Date());
  const isExpired = daysUntilExpiration < 0;
  const isUrgent = daysUntilExpiration <= 30 && daysUntilExpiration >= 0;

  const getProbabilityColor = (probability: number | null) => {
    if (!probability) return "default";
    if (probability >= 75) return "default";
    if (probability >= 40) return "secondary";
    return "destructive";
  };

  const getProbabilityLabel = (probability: number | null) => {
    if (!probability) return "Not Assessed";
    if (probability >= 75) return "High";
    if (probability >= 40) return "Medium";
    return "Low";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          Contract Renewal
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Expiration</span>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="font-medium">
                {isExpired
                  ? `Expired ${formatDistanceToNow(new Date(endDate), { addSuffix: true })}`
                  : `${daysUntilExpiration} days remaining`}
              </span>
              {isUrgent && <Badge variant="destructive">Urgent</Badge>}
            </div>
          </div>

          <Progress
            value={isExpired ? 100 : Math.max(0, 100 - (daysUntilExpiration / 90) * 100)}
            className="h-2"
          />
        </div>

        {renewal && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Renewal Probability</span>
              <Badge variant={getProbabilityColor(renewal.renewal_probability) as any}>
                {getProbabilityLabel(renewal.renewal_probability)}
                {renewal.renewal_probability && ` (${renewal.renewal_probability}%)`}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant="outline">{renewal.renewal_status}</Badge>
            </div>

            {renewal.next_reminder_at && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                Next reminder: {formatDistanceToNow(new Date(renewal.next_reminder_at), { addSuffix: true })}
              </div>
            )}

            {renewal.notes && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">{renewal.notes}</p>
              </div>
            )}

            {renewal.renewal_status === "pending" && (
              <div className="space-y-3 pt-2 border-t">
                <Textarea
                  placeholder="Add notes about renewal discussion..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      updateRenewalMutation.mutate({ status: "in_progress", notes })
                    }
                    disabled={updateRenewalMutation.isPending}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Initiate Renewal
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() =>
                      updateRenewalMutation.mutate({ status: "declined", notes })
                    }
                    disabled={updateRenewalMutation.isPending}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Mark as Declined
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
